import { useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { faPaperclip } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Form from 'epn-ui/umd/dist/components/Form';
import Upload from 'epn-ui/umd/dist/components/Upload';

// Helpers
import { TooltipPro } from 'epn-ui/umd/dist/components/TooltipPro';
import useI18n from '@i18n';
import { IGetCdnUrl } from '@api/types';
import api from '@api';

// Components
import BaseToast from '@base/BaseToast';

// Config
import {
    NOT_IMAGE_FILE_EXT,
    UPLOAD_FILE_EXT_SUPPORT,
    UPLOAD_IMAGES_LIMIT,
    UPLOAD_IMAGE_SIZE_LIMIT,
} from '../config';

export interface IUploadFilesProps {
    loading?: boolean;
    fileContainerId: string;
    onProgress?: (progress: boolean) => void;
}

const { UploadItem } = Upload;

const UploadFiles: React.FC<IUploadFilesProps> = ({ loading, fileContainerId, onProgress }) => {
    const [uploadIsProgress, setUploadIsProgress] = useState<boolean>(false);
    const [currentFilesCount, setCurrentFilesCount] = useState<number>(0);
    const { t } = useI18n('Support');

    /**
     * @param {boolean} progress
     * @returns {void}
     */
    const onProgressHandler = (progress: boolean): void => {
        setUploadIsProgress(progress);
        if (typeof onProgress === 'function') {
            onProgress(progress);
        }
    };

    return (
        <>
            {loading && <div className="w-24 h-4 mb-2 bg-gray-300 rounded-2 animate-pulse" />}
            {!loading && (
                <Form.Item name="files" className="mb-0">
                    <Upload
                        className="max-h-10"
                        accept={UPLOAD_FILE_EXT_SUPPORT}
                        multiple
                        maxCount={UPLOAD_IMAGES_LIMIT}
                        beforeUpload={file => {
                            if (file.size > UPLOAD_IMAGE_SIZE_LIMIT) {
                                toast.error(
                                    <BaseToast
                                        text={t('The maximum file size is 2 mb')}
                                        type="error"
                                    />,
                                    {
                                        toastId: `${file.name}-${file.size}`,
                                    },
                                );
                                return Upload.LIST_IGNORE;
                            }

                            return true;
                        }}
                        renderItem={(_originNode, file, _fileList, actions) =>
                            createPortal(
                                <UploadItem
                                    file={file}
                                    actions={actions}
                                    loading={uploadIsProgress}
                                />,
                                document.getElementById(fileContainerId),
                            )
                        }
                        onChange={options => {
                            if (options?.file?.status === 'removed') {
                                setCurrentFilesCount(currentFilesCount - 1);
                            }
                        }}
                        customRequest={async options => {
                            const { onSuccess, onError, file, filename } = options;

                            if (currentFilesCount === UPLOAD_IMAGES_LIMIT) {
                                toast.error(
                                    <BaseToast
                                        text={t(
                                            'You can upload no more than 3 files up to 2 MB in size',
                                        )}
                                        type="error"
                                    />,
                                );
                                return;
                            }

                            try {
                                onProgressHandler(true);
                                setCurrentFilesCount(currentFilesCount + 1);
                                let type = 'image';

                                if (typeof file !== 'string') {
                                    const fileExt = file.type.split('/')[1];
                                    type = NOT_IMAGE_FILE_EXT.includes(fileExt) ? 'file' : 'image';
                                }

                                const cdnData = await api.cdn.getUploadUrl<IGetCdnUrl>({
                                    params: {
                                        visibility: 'private',
                                        type,
                                    },
                                });

                                const formData = new FormData();

                                formData.append('file', file);
                                formData.append('token', cdnData.data.attributes.token);
                                const uploadResponse = await fetch(
                                    cdnData.data.attributes.uploadUrl,
                                    {
                                        method: 'POST',
                                        body: formData,
                                        credentials: 'include',
                                    },
                                );

                                if (!uploadResponse.ok) {
                                    throw new Error('Support upload failed');
                                }

                                const response = await uploadResponse.json();
                                onSuccess(response.data[0].id);

                                toast.success(
                                    <BaseToast
                                        text={t('The file has been successfully uploaded')}
                                        type="success"
                                    />,
                                    {
                                        toastId: filename,
                                    },
                                );

                                onProgressHandler(false);
                            } catch (err) {
                                toast.error(
                                    <BaseToast
                                        text={t('An error occurred while loading the file')}
                                        type="error"
                                    />,
                                    {
                                        toastId: filename,
                                    },
                                );
                                onProgressHandler(false);
                                onError(err);
                            }
                        }}
                    >
                        <div className="hidden xs:block">
                            <TooltipPro
                                placement="top"
                                title={t('You can upload no more than 3 files up to 2 MB in size')}
                            >
                                <div className="cursor-pointer text-blueGray-500 text-p1">
                                    <FontAwesomeIcon icon={faPaperclip} /> {t('Attach file')}
                                </div>
                            </TooltipPro>
                        </div>

                        <div className="block cursor-pointer xs:hidden text-blueGray-500 text-p1">
                            <FontAwesomeIcon icon={faPaperclip} /> {t('Attach file')}
                        </div>
                    </Upload>
                </Form.Item>
            )}
        </>
    );
};

export default UploadFiles;
